import { useState, useCallback, useEffect, useRef } from 'react';
import {
  BreadTransferSession,
  BreadTransferConfig,
  TransferShift,
  UpdateTransferRowPayload,
  BreadTransferRow
} from '../types/bread-transfers.types';
import {
  mockBreadTransferConfig
} from '../mocks/bread-transfers.mock';
import {
  fetchSession,
  createSessionApi,
  updateRowApi,
  completeSessionApi,
  resetSessionApi
} from '../services/breadTransfers.service';
import type { ApiSession, ApiRow } from '../services/breadTransfers.service';

// Debounce timers por rowId+field
const debounceTimers: Record<string, ReturnType<typeof setTimeout>> = {};

// Parsea "MAC 01 (ANGAMOS)" → { code: "MAC 01", name: "ANGAMOS" }
const parseLocationName = (nombre: string): { code: string; name: string } => {
  const match = nombre?.match(/^(MAC \d+)\s*\((.+)\)$/);
  if (match) return { code: match[1], name: match[2] };
  return { code: nombre || '', name: nombre || '' };
};

const mapApiToSession = (api: ApiSession, shift: TransferShift): BreadTransferSession => {
  const locations: BreadTransferRow[] = (api.rows || []).map((row: ApiRow) => {
    const parsed = parseLocationName(row.location?.nombre || '');
    return {
      id: row.id,
      locationId: String(row.locationId),
      locationCode: parsed.code,
      locationName: parsed.name,
      parameterPreviousDay: row.parameterPreviousDay || 0,
      initialStock: row.initialStock || 0,
      waste: row.waste || 0,
      previousDayDispatch: row.previousDayDispatch || 0,
      fixedDispatch: row.fixedDispatch || 0,
      calculatedDispatch: row.calculatedDispatch || 0
    };
  });

  const totals = locations.reduce((acc, loc) => ({
    parameterPreviousDay: acc.parameterPreviousDay + loc.parameterPreviousDay,
    initialStock: acc.initialStock + loc.initialStock,
    waste: acc.waste + loc.waste,
    previousDayDispatch: acc.previousDayDispatch + loc.previousDayDispatch,
    fixedDispatch: acc.fixedDispatch + loc.fixedDispatch,
    calculatedDispatch: acc.calculatedDispatch + loc.calculatedDispatch
  }), {
    parameterPreviousDay: 0, initialStock: 0, waste: 0,
    previousDayDispatch: 0, fixedDispatch: 0, calculatedDispatch: 0
  });

  return {
    id: String(api.id),
    date: api.date,
    shift: shift as 'morning' | 'afternoon',
    locations,
    totals,
    status: api.status,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt
  };
};

export const useBreadTransfers = (selectedDate: string = new Date().toISOString().split('T')[0]) => {
  const [config] = useState<BreadTransferConfig>(mockBreadTransferConfig);
  const [sessions, setSessions] = useState<BreadTransferSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedRef = useRef<string>('');

  // Cargar sesiones desde API al cambiar la fecha
  useEffect(() => {
    const key = selectedDate;
    if (loadedRef.current === key) return;
    loadedRef.current = key;

    const loadSessions = async () => {
      setLoading(true);
      setError(null);
      try {
        const shifts: TransferShift[] = ['morning', 'afternoon'];
        const results = await Promise.all(
          shifts.map(async (shift) => {
            const apiSession = await fetchSession(selectedDate, shift);
            if (apiSession) {
              return mapApiToSession(apiSession, shift);
            }
            return null;
          })
        );

        const validSessions = results.filter((s): s is BreadTransferSession => s !== null);
        setSessions(prev => {
          // Mantener sesiones de otras fechas y reemplazar las de esta fecha
          const otherDates = prev.filter(s => s.date !== selectedDate);
          return [...otherDates, ...validSessions];
        });
      } catch (err) {
        console.error('Error loading bread transfer sessions:', err);
        setError(err instanceof Error ? err.message : 'Error cargando sesiones');
      } finally {
        setLoading(false);
      }
    };

    loadSessions();
  }, [selectedDate]);

  const calculateDispatch = useCallback((row: Omit<BreadTransferRow, 'calculatedDispatch'>): number => {
    return Math.max(0, row.initialStock - row.waste + row.parameterPreviousDay - row.previousDayDispatch + row.fixedDispatch);
  }, []);

  const calculateTotals = useCallback((locations: BreadTransferRow[]) => {
    return locations.reduce((totals, location) => ({
      parameterPreviousDay: totals.parameterPreviousDay + location.parameterPreviousDay,
      initialStock: totals.initialStock + location.initialStock,
      waste: totals.waste + location.waste,
      previousDayDispatch: totals.previousDayDispatch + location.previousDayDispatch,
      fixedDispatch: totals.fixedDispatch + location.fixedDispatch,
      calculatedDispatch: totals.calculatedDispatch + location.calculatedDispatch
    }), {
      parameterPreviousDay: 0, initialStock: 0, waste: 0,
      previousDayDispatch: 0, fixedDispatch: 0, calculatedDispatch: 0
    });
  }, []);

  const getSession = useCallback((shift: TransferShift): BreadTransferSession | null => {
    return sessions.find(session =>
      session.date === selectedDate && session.shift === shift
    ) || null;
  }, [sessions, selectedDate]);

  const createSession = useCallback(async (shift: TransferShift): Promise<BreadTransferSession> => {
    setLoading(true);
    setError(null);
    try {
      const apiSession = await createSessionApi(selectedDate, shift);
      const session = mapApiToSession(apiSession, shift);
      setSessions(prev => [...prev.filter(s => !(s.date === selectedDate && s.shift === shift)), session]);
      return session;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creando sesión');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const updateTransferRow = useCallback(async (
    shift: TransferShift,
    payload: UpdateTransferRowPayload
  ): Promise<void> => {
    // Actualización optimista local
    setSessions(prev => prev.map(session => {
      if (session.date !== selectedDate || session.shift !== shift) {
        return session;
      }

      const updatedLocations = session.locations.map(location => {
        if (location.locationId !== payload.locationId) {
          return location;
        }

        const updatedLocation = {
          ...location,
          [payload.field]: payload.value
        };
        updatedLocation.calculatedDispatch = calculateDispatch(updatedLocation);
        return updatedLocation;
      });

      return {
        ...session,
        locations: updatedLocations,
        totals: calculateTotals(updatedLocations),
        updatedAt: new Date().toISOString()
      };
    }));

    // Debounce la llamada a la API
    const session = sessions.find(s => s.date === selectedDate && s.shift === shift);
    if (!session) return;

    const row = session.locations.find(l => l.locationId === payload.locationId);
    if (!row?.id) return;

    const sessionId = Number(session.id);
    const rowId = row.id;
    const timerKey = `${rowId}-${payload.field}`;

    if (debounceTimers[timerKey]) {
      clearTimeout(debounceTimers[timerKey]);
    }

    debounceTimers[timerKey] = setTimeout(async () => {
      try {
        await updateRowApi(sessionId, rowId, payload.field, payload.value);

        // Si el campo afecta calculatedDispatch, también persistir el cálculo
        const editableFields: string[] = ['parameterPreviousDay', 'initialStock', 'waste', 'previousDayDispatch', 'fixedDispatch'];
        if (editableFields.includes(payload.field)) {
          // Recalcular con datos actuales
          const currentSession = sessions.find(s => s.date === selectedDate && s.shift === shift);
          const currentRow = currentSession?.locations.find(l => l.locationId === payload.locationId);
          if (currentRow) {
            const updatedRow = { ...currentRow, [payload.field]: payload.value };
            const newDispatch = Math.max(0, updatedRow.initialStock - updatedRow.waste + updatedRow.parameterPreviousDay - updatedRow.previousDayDispatch + updatedRow.fixedDispatch);
            await updateRowApi(sessionId, rowId, 'calculatedDispatch', newDispatch);
          }
        }
      } catch (err) {
        console.error('Error persisting row update:', err);
        setError(err instanceof Error ? err.message : 'Error guardando cambio');
      }
      delete debounceTimers[timerKey];
    }, 300);
  }, [selectedDate, sessions, calculateDispatch, calculateTotals]);

  const completeSession = useCallback(async (shift: TransferShift): Promise<void> => {
    const session = sessions.find(s => s.date === selectedDate && s.shift === shift);
    if (!session) throw new Error('Sesión no encontrada');

    setLoading(true);
    setError(null);
    try {
      await completeSessionApi(Number(session.id));
      setSessions(prev => prev.map(s => {
        if (s.date === selectedDate && s.shift === shift) {
          return { ...s, status: 'completed' as const, updatedAt: new Date().toISOString() };
        }
        return s;
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error completando sesión');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedDate, sessions]);

  const resetSession = useCallback(async (shift: TransferShift): Promise<void> => {
    const session = sessions.find(s => s.date === selectedDate && s.shift === shift);
    if (!session) throw new Error('Sesión no encontrada');

    setLoading(true);
    setError(null);
    try {
      await resetSessionApi(Number(session.id));
      setSessions(prev => prev.map(s => {
        if (s.date === selectedDate && s.shift === shift) {
          return { ...s, status: 'draft' as const, updatedAt: new Date().toISOString() };
        }
        return s;
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error reiniciando sesión');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedDate, sessions]);

  return {
    config,
    sessions,
    loading,
    error,
    getSession,
    createSession,
    updateTransferRow,
    completeSession,
    resetSession
  };
};
